import { useEffect, useState } from "react";
import { useUploadPreprocessor } from "../../UploadPreprocessorContext";
import { BlobReader, ZipReader } from "@zip.js/zip.js";
import {
  fbxExtensions,
  shapefileExtensions,
  cesium3dTileExtensions,
} from "../../lib/zip-file-inspectors";
import {
  getFileExtensions,
  getFilenames,
  getFilePaths,
} from "../../lib/zip-file-inspectors/utils";
import { UploadIssueFileType } from "../../lib/types/UploadIssueType";
import AssetTypeIcon from "components/elements/Chip/AssetTypeIcon";
import OverflowTooltip from "components/elements/Tooltips/OverflowTooltip";
import { getFileExtension } from "lib/actions/files";
import RequiredFileCard from "../RequiredFileCard";

const ZipFileUploadIssue = () => {
  const { selectedUploadIssue } = useUploadPreprocessor();
  const [requiredFileExtensions, setRequiredFileExtensions] = useState<
    Set<string>
  >(new Set());
  const [presentRequiredFileExtensions, setPresentRequiredFileExtensions] =
    useState<string[]>([]);
  const [missingRequiredFileExtensions, setMissingRequiredFileExtensions] =
    useState<string[]>([]);
  const [zippedFilenames, setZippedFilenames] = useState<string[]>([]);

  useEffect(() => {
    if (!selectedUploadIssue.file) return;

    new ZipReader(new BlobReader(selectedUploadIssue.file))
      .getEntries()
      .then((entries) => {
        var filenames = getFilenames(getFilePaths(entries));
        setZippedFilenames(filenames);
        const fileExtensions = getFileExtensions(filenames);
        if (selectedUploadIssue.type === UploadIssueFileType.SHAPEFILE) {
          setRequiredFileExtensions(shapefileExtensions.required);
          setPresentRequiredFileExtensions(
            getPresentInRequiredList(
              fileExtensions,
              shapefileExtensions.required,
            ),
          );
          setMissingRequiredFileExtensions(
            getMissingFromRequiredList(
              fileExtensions,
              shapefileExtensions.required,
            ),
          );
        } else if (selectedUploadIssue.type === UploadIssueFileType.FBX) {
          setRequiredFileExtensions(fbxExtensions.required);
          setPresentRequiredFileExtensions(
            getPresentInRequiredList(fileExtensions, fbxExtensions.required),
          );
          setMissingRequiredFileExtensions(
            getMissingFromRequiredList(fileExtensions, fbxExtensions.required),
          );
        } else if (selectedUploadIssue.type === UploadIssueFileType.TILESET) {
          setRequiredFileExtensions(cesium3dTileExtensions.required);
          setPresentRequiredFileExtensions(
            getPresentInRequiredList(
              fileExtensions,
              cesium3dTileExtensions.required,
            ),
          );
          setMissingRequiredFileExtensions(
            getMissingFromRequiredList(
              fileExtensions,
              cesium3dTileExtensions.required,
            ),
          );
        }
      });
  }, []);

  return (
    <>
      <div className="space-y-4">
        <p>
          To upload {selectedUploadIssue.type} to Keyframe, please ensure your
          ZIP archive includes all the required file types. Required file types:
        </p>
        <ul className="flex flex-wrap">
          {Array.from(requiredFileExtensions).map((fileExtension, index) => (
            <li key={index} className="mx-4 font-mono">
              .{fileExtension}
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-md border p-3">
        <div className="flex flex-col">
          <div className="flex items-center space-x-3">
            <div className="rounded-md border p-1">
              <AssetTypeIcon
                ext={getFileExtension(selectedUploadIssue.file.name)}
              />
            </div>
            <OverflowTooltip className="font-semibold">
              {selectedUploadIssue.file.name}
            </OverflowTooltip>
          </div>
          <div className="space-x-2 p-3">
            <span className="text-warning-500">&#x2022;</span>
            <span>
              {presentRequiredFileExtensions.length}
              &nbsp;/&nbsp;
              {requiredFileExtensions.size}
            </span>
            <span>required files included</span>
          </div>
        </div>
        <div className="-mr-3 flex max-h-72 flex-col space-y-3 overflow-y-scroll pr-3">
          {missingRequiredFileExtensions.map((fileExtension) => (
            <RequiredFileCard
              key={fileExtension}
              fileName={fileExtension}
              fileExtension={fileExtension}
              missing={true}
            />
          ))}
          {zippedFilenames
            .filter((name) =>
              requiredFileExtensions.has(getFileExtension(name)),
            )
            .map((filename) => (
              <RequiredFileCard key={filename} fileName={filename} />
            ))}
        </div>
      </div>
    </>
  );
};

function getPresentInRequiredList(
  target: string[],
  requiredSet: Set<string>,
): string[] {
  return Array.from(requiredSet).filter((requiredItem) =>
    target.includes(requiredItem),
  );
}

function getMissingFromRequiredList(
  target: string[],
  requiredSet: Set<string>,
): string[] {
  return Array.from(requiredSet).filter(
    (requiredItem) => !target.includes(requiredItem),
  );
}

export default ZipFileUploadIssue;
